import { EditableProject } from "@/reducers/environments";
import { NEW_PROJECT_ENVIRONMENTS } from "./constants";
import { AddTarget, CreatedEnvironment } from "./types";

export type SaveProjectRequest = {
  ID: string | null;
  name: string;
  /** Saves only the listed environments, leaving the project row as it is. */
  environmentsOnly?: boolean;
  /**
   * Echoed with environmentsOnly so a backend that predates it, and saves the
   * whole project row, doesn't clear the project's code evaluation config.
   */
  codeEval?: EditableProject["codeEval"];
  /** The first environment is the one the wizard switches to. */
  environments: { ID: null; name: string; projectID: string | null }[];
};

/** save_project echoes the saved environments with their assigned IDs. */
export type SaveProjectResponse = {
  ID: string;
  environments: { ID: string; name: string }[];
};

export type NewEnvironmentInput =
  | { target: "project"; name: string }
  | { target: "environment"; name: string; project: EditableProject };

function sameName(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

export function validateName(
  target: AddTarget,
  name: string,
  projects: EditableProject[],
  project: EditableProject | undefined,
): string | null {
  const trimmed = name.trim();
  if (!trimmed) {
    return target === "project"
      ? "Enter a name for the project"
      : "Enter a name for the environment";
  }
  if (
    target === "project" &&
    projects.some((existing) => sameName(existing.name, trimmed))
  ) {
    return `A project named “${trimmed}” already exists`;
  }
  if (
    target === "environment" &&
    project?.environments.some((existing) => sameName(existing.name, trimmed))
  ) {
    return `${project.name} already has an environment named “${trimmed}”`;
  }
  return null;
}

export function buildSaveProjectRequest(
  input: NewEnvironmentInput,
): SaveProjectRequest {
  const name = input.name.trim();
  if (input.target === "project") {
    return {
      ID: null,
      name,
      environments: NEW_PROJECT_ENVIRONMENTS.map((envName) => ({
        ID: null,
        name: envName,
        projectID: null,
      })),
    };
  }
  const { project } = input;
  return {
    ID: project.ID,
    name: project.name,
    environmentsOnly: true,
    codeEval: project.codeEval,
    environments: [{ ID: null, name, projectID: project.ID }],
  };
}

export function createdFromResponse(
  response: SaveProjectResponse,
  request: SaveProjectRequest,
  projectName: string,
): CreatedEnvironment | null {
  const env = response.environments?.[0];
  if (!response.ID || !env?.ID) {
    return null;
  }
  return {
    projectID: response.ID,
    projectName,
    envID: env.ID,
    envName: env.name || request.environments[0].name,
  };
}

/**
 * Finds the environment a save created by comparing account metadata from
 * before and after it, for when the save's response was lost. Returns null
 * unless exactly one project and one environment match, so a concurrent save
 * with the same names can't be mistaken for this one.
 */
export function findCreatedEnvironment(
  before: EditableProject[],
  after: EditableProject[],
  request: SaveProjectRequest,
): CreatedEnvironment | null {
  const existingEnvIDs = new Set(
    before.flatMap((project) => project.environments.map((env) => env.ID)),
  );
  const projects = request.ID
    ? after.filter((candidate) => candidate.ID === request.ID)
    : after.filter(
        (candidate) =>
          !before.some((existing) => existing.ID === candidate.ID) &&
          candidate.name === request.name,
      );
  if (projects.length !== 1) {
    return null;
  }
  const [project] = projects;
  const envName = request.environments[0]?.name;
  const envs = project.environments.filter(
    (candidate) =>
      candidate.ID !== null &&
      !existingEnvIDs.has(candidate.ID) &&
      candidate.name === envName,
  );
  if (!project.ID || envs.length !== 1 || !envs[0].ID) {
    return null;
  }
  return {
    projectID: project.ID,
    projectName: project.name,
    envID: envs[0].ID,
    envName: envs[0].name,
  };
}
